import type { FileRecord, Transfer } from './types';

export interface QueueStorage {
  list(): Promise<Transfer[]>;
  files(id: string): Promise<FileRecord | undefined>;
  edit<T>(change: (jobs: Map<string, Transfer>, files: { put: (record: FileRecord) => void; delete: (id: string) => void }) => T): Promise<T>;
}

/** Metadata is read independently of potentially large file bodies. All edits are atomic. */
export class IndexedQueueStorage implements QueueStorage {
  private opening?: Promise<IDBDatabase>;
  private database() {
    return this.opening ||= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('huckleberry-transfers', 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore('jobs', { keyPath: 'id' });
        request.result.createObjectStore('files', { keyPath: 'id' });
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); this.opening = undefined; };
        resolve(db);
      };
      request.onerror = () => { this.opening = undefined; reject(request.error); };
      request.onblocked = () => { this.opening = undefined; reject(new Error('Bitte andere Huckleberry-Tabs neu laden.')); };
    });
  }
  async list(): Promise<Transfer[]> {
    const db = await this.database();
    return new Promise((resolve, reject) => {
      const req = db.transaction('jobs').objectStore('jobs').getAll();
      req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error);
    });
  }
  async files(id: string): Promise<FileRecord | undefined> {
    const db = await this.database();
    return new Promise((resolve, reject) => {
      const req = db.transaction('files').objectStore('files').get(id);
      req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error);
    });
  }
  async edit<T>(change: (jobs: Map<string, Transfer>, files: { put: (record: FileRecord) => void; delete: (id: string) => void }) => T): Promise<T> {
    const db = await this.database();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['jobs', 'files'], 'readwrite');
      const jobsStore = tx.objectStore('jobs'), fileStore = tx.objectStore('files');
      const req = jobsStore.getAll();
      let result: T; let failure: unknown;
      req.onsuccess = () => {
        try {
          const before = new Map<string, Transfer>(req.result.map(job => [job.id, job]));
          const after = new Map(before);
          result = change(after, { put: record => { fileStore.put(record); }, delete: id => { fileStore.delete(id); } });
          for (const id of Array.from(before.keys())) if (!after.has(id)) jobsStore.delete(id);
          for (const [id, job] of Array.from(after)) if (before.get(id) !== job) jobsStore.put(job);
        } catch (error) { failure = error; tx.abort(); }
      };
      tx.oncomplete = () => resolve(result);
      tx.onerror = tx.onabort = () => reject(failure || tx.error || new Error('Speichern fehlgeschlagen'));
    });
  }
}
